import { z } from 'zod';
import { FEATURE_KEYS } from './features.js';
import { paginated, resource } from './pagination.js';
/**
 * Integrations marketplace contracts (M3.2-M3.6, API.md §2.10 and §2.13,
 * INTEGRATIONS.md §10, DATABASE.md §7.10).
 *
 * Two rules shape everything in this file:
 *
 * 1. **A credential is never a response field.** There is a schema for *writing*
 *    credentials and none for reading them; what comes back is `credHint`, a
 *    display-only object of last-4s and masked addresses (SECURITY.md §7.4). If you
 *    find yourself wanting a `credentials` field on a view model, the answer is no.
 * 2. **The catalog's schemas are data, not types.** `settingsSchema` and
 *    `credentialSchema` are JSON Schema documents authored by a super admin and
 *    rendered by the clinic admin's form, so they are validated *as JSON Schema*
 *    here - shape, allowed keywords, nesting depth and size - and then used to
 *    validate the values a clinic submits. That validation happens on the server;
 *    the browser's copy is a convenience.
 */
/* ------------------------------------------------------------------ vocabularies */
/** `integrations.category` (`ck_integrations_category`). */
export const INTEGRATION_CATEGORIES = [
    'ehr',
    'eligibility',
    'payment',
    'email',
    'sms',
    /** Marketing CRM (ADR-028). Added with `@platform/integration-highlevel`. */
    'crm',
    'ai_estimate',
    'analytics',
];
export const IntegrationCategorySchema = z.enum(INTEGRATION_CATEGORIES);
/** `integrations.status` (`ck_integrations_status`). */
export const CATALOG_STATUSES = ['available', 'beta', 'deprecated', 'disabled'];
export const CatalogStatusSchema = z.enum(CATALOG_STATUSES);
/**
 * `integration_installs.health` (`ck_integration_installs_health`, INTEGRATIONS.md
 * §10.2). `configured` is the state between "credentials saved and `testConnection()`
 * passed once" and the first scheduled health check.
 */
export const INSTALL_HEALTH_STATUSES = [
    'unconfigured',
    'configured',
    'healthy',
    'degraded',
    'failed',
];
export const InstallHealthSchema = z.enum(INSTALL_HEALTH_STATUSES);
/** `integration_calls.status` (`ck_integration_calls_status`). */
export const INTEGRATION_CALL_STATUSES = ['ok', 'error', 'timeout', 'rate_limited'];
export const IntegrationCallStatusSchema = z.enum(INTEGRATION_CALL_STATUSES);
/** `integration_health_checks.result`. */
export const HealthCheckResultSchema = z.enum(['healthy', 'degraded', 'failed']);
/** The plan gate: a feature key from `features.ts`, or null for an ungated adapter. */
export const PlanFeatureKeySchema = z.enum(FEATURE_KEYS).nullable();
/** `integrations.key`: also the adapter folder name under `packages/integrations`. */
export const IntegrationKeySchema = z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/, 'lower-case letters, digits and hyphens');
/* ------------------------------------------------------------------ JSON Schema */
/**
 * How large a catalog schema may be, serialized.
 *
 * The cap is not about storage - it is about the form the clinic admin renders and
 * the validator that walks it. A schema is authored by a super admin, so this is not
 * an untrusted-input boundary, but a 4 MB `properties` map would still turn one bad
 * paste into an unusable page and a slow request for every install.
 */
export const CATALOG_SCHEMA_MAX_BYTES = 16 * 1024;
/** Properties one schema may declare. */
export const CATALOG_SCHEMA_MAX_PROPERTIES = 60;
const JSON_SCHEMA_TYPES = ['string', 'number', 'integer', 'boolean', 'array', 'object'];
/**
 * One property of a catalog schema: the draft-07 keywords the admin form knows how to
 * render, plus our own `secret`.
 *
 * Unknown keywords are rejected rather than ignored. A schema whose `oneOf` the form
 * silently drops is worse than one that fails to save: the clinic would see a field
 * that validates differently on the server than it looks on the screen.
 */
export const CatalogSchemaPropertySchema = z.lazy(() => z
    .object({
    type: z.enum(JSON_SCHEMA_TYPES),
    title: z.string().max(120).optional(),
    description: z.string().max(500).optional(),
    /** Ours, not draft-07: the field is encrypted and never returned (§10.1). */
    secret: z.boolean().optional(),
    format: z.enum(['email', 'uri', 'date', 'date-time', 'hostname']).optional(),
    pattern: z.string().max(200).optional(),
    enum: z
        .array(z.union([z.string(), z.number(), z.boolean()]))
        .max(50)
        .optional(),
    default: z.unknown().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    minLength: z.number().int().min(0).max(10_000).optional(),
    maxLength: z.number().int().min(0).max(10_000).optional(),
    /** Only one level: `array of scalars`, which is every list the catalog needs. */
    items: z
        .object({
        type: z.enum(JSON_SCHEMA_TYPES),
        enum: z
            .array(z.union([z.string(), z.number(), z.boolean()]))
            .max(50)
            .optional(),
        pattern: z.string().max(200).optional(),
    })
        .optional(),
})
    .strict());
/** Branches one `oneOf` or `anyOf` may list. */
export const CATALOG_SCHEMA_MAX_VARIANTS = 8;
/**
 * One branch of a top-level `oneOf`/`anyOf`: an object variant that says *which of the
 * declared properties must be present*, and nothing else.
 *
 * This is the whole of the "either/or" vocabulary the catalog needs, and the reason it
 * is this narrow is the same reason the property dialect is narrow. A branch that could
 * also redeclare `properties` would be a second, shadow schema for the same field - the
 * form would render one set of constraints and the server would enforce another. A
 * branch that only names `required` is exactly a radio group: "which sender?", "which
 * kind of Twilio auth?", with `title` as the label for the choice.
 *
 * `type: 'object'` is accepted and ignored: a bare `{ "required": [...] }` is already an
 * object-only assertion in JSON Schema 2020-12, and writing the type out is common.
 *
 * Two narrow additions, for a choice the admin makes with a field rather than by which
 * boxes they fill (HighLevel's `kind`):
 *
 * - `when`: the branch applies only while these properties hold these values. The form
 *   shows the chosen branch's fields and no others, and the server does not count a
 *   branch whose `when` does not match - so a value left in a hidden box cannot make a
 *   second branch "hold".
 * - `optional`: fields that belong to the branch without being required, so the form
 *   knows to show them with it.
 *
 * Neither redeclares a property, so there is still exactly one definition of each field.
 */
export const CatalogSchemaVariantSchema = z
    .object({
    type: z.literal('object').optional(),
    title: z.string().max(120).optional(),
    description: z.string().max(500).optional(),
    when: z.record(z.string().min(1).max(64), z.string().min(1).max(120)).optional(),
    required: z.array(z.string().min(1).max(64)).min(1).max(CATALOG_SCHEMA_MAX_PROPERTIES),
    optional: z.array(z.string().min(1).max(64)).max(CATALOG_SCHEMA_MAX_PROPERTIES).optional(),
})
    .strict();
/**
 * A settings or credential schema: a draft-07 **object** schema and nothing else.
 *
 * `type: 'object'` is required rather than inferred. The catalog describes a form, and
 * a form is a set of named fields; a top-level array schema has no rendering and no
 * obvious validation story for a partial `PATCH /settings`.
 *
 * ## The three document-level keywords, and why they are here (M13.5)
 *
 * `oneOf`, `anyOf` and `dependentRequired` say things about the *document* that no
 * per-property keyword can: "exactly one sender", "either an auth token or an API key
 * pair", "an API key SID is useless without its secret". Twilio's seeded row needs all
 * three, and until this the row simply did not parse - `CatalogService` skipped it and
 * `catalog.require('twilio')` answered 404, so the adapter was unreachable through the
 * API (`Docs/TESTING.md` §4.22).
 *
 * They are admitted here with 2020-12 semantics and enforced in
 * `apps/api/src/integrations/schema-validator.ts`. The admin form renders the *fields*
 * they constrain and not the choice itself; `Docs/ADMIN_INTEGRATIONS_UI.md` records
 * which keywords the form draws and which are server-enforced only.
 */
export const CatalogJsonSchemaSchema = z
    .object({
    $schema: z.literal('http://json-schema.org/draft-07/schema#').optional(),
    type: z.literal('object'),
    title: z.string().max(120).optional(),
    description: z.string().max(500).optional(),
    properties: z.record(z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z][A-Za-z0-9_]*$/), CatalogSchemaPropertySchema),
    required: z.array(z.string().min(1).max(64)).max(CATALOG_SCHEMA_MAX_PROPERTIES).optional(),
    additionalProperties: z.boolean().optional(),
    /** Exactly one branch must hold. */
    oneOf: z.array(CatalogSchemaVariantSchema).min(2).max(CATALOG_SCHEMA_MAX_VARIANTS).optional(),
    /** At least one branch must hold. */
    anyOf: z.array(CatalogSchemaVariantSchema).min(2).max(CATALOG_SCHEMA_MAX_VARIANTS).optional(),
    /** `{ trigger: [alsoRequired, …] }`: present `trigger` makes the others required. */
    dependentRequired: z
        .record(z.string().min(1).max(64), z.array(z.string().min(1).max(64)).min(1).max(CATALOG_SCHEMA_MAX_PROPERTIES))
        .optional(),
})
    .strict()
    .refine((schema) => Object.keys(schema.properties).length <= CATALOG_SCHEMA_MAX_PROPERTIES, {
    error: `a schema may declare at most ${CATALOG_SCHEMA_MAX_PROPERTIES} properties`,
})
    .refine((schema) => (schema.required ?? []).every((name) => name in schema.properties), {
    error: 'every name in `required` must be declared in `properties`',
})
    .refine((schema) => [...(schema.oneOf ?? []), ...(schema.anyOf ?? [])].every((variant) => [
    ...variant.required,
    ...(variant.optional ?? []),
    ...Object.keys(variant.when ?? {}),
].every((name) => name in schema.properties)), {
    error: 'every name a `oneOf`/`anyOf` branch requires, lists as optional or tests in `when` must be declared in `properties`',
})
    .refine((schema) => Object.entries(schema.dependentRequired ?? {}).every(([trigger, dependents]) => trigger in schema.properties && dependents.every((name) => name in schema.properties)), { error: 'every name in `dependentRequired` must be declared in `properties`' })
    .refine((schema) => JSON.stringify(schema).length <= CATALOG_SCHEMA_MAX_BYTES, {
    error: `a schema must serialize to at most ${CATALOG_SCHEMA_MAX_BYTES} bytes`,
});
/** The names a credential schema marks `secret: true`: encrypted, never returned. */
export function secretFieldNames(schema) {
    return Object.entries(schema.properties)
        .filter(([, property]) => property.secret === true)
        .map(([name]) => name);
}
/* --------------------------------------------------------------------- catalog */
/** One row of `integrations` as the platform API returns it (API.md §2.13). */
export const CatalogEntrySchema = z.object({
    id: z.uuid(),
    key: IntegrationKeySchema,
    category: IntegrationCategorySchema,
    name: z.string().min(1).max(120),
    vendor: z.string().max(120).nullable(),
    description: z.string().max(1000).nullable(),
    /** Adapter contract version; a major bump means the install must be re-configured. */
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    capabilities: z.array(z.string().min(1).max(64)).max(64),
    settingsSchema: CatalogJsonSchemaSchema,
    credentialSchema: CatalogJsonSchemaSchema,
    /** The plan gate (`plan_features` / `tenant_features`), or null when ungated. */
    planFeatureKey: PlanFeatureKeySchema,
    docsUrl: z.url().max(500).nullable(),
    status: CatalogStatusSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** `POST /platform/integrations`. */
export const CreateCatalogEntrySchema = z.object({
    key: IntegrationKeySchema,
    category: IntegrationCategorySchema,
    name: z.string().min(1).max(120),
    vendor: z.string().max(120).nullish(),
    description: z.string().max(1000).nullish(),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    capabilities: z.array(z.string().min(1).max(64)).max(64).default([]),
    settingsSchema: CatalogJsonSchemaSchema,
    credentialSchema: CatalogJsonSchemaSchema,
    planFeatureKey: PlanFeatureKeySchema.default(null),
    docsUrl: z.url().max(500).nullish(),
    status: CatalogStatusSchema.default('available'),
});
/**
 * `PATCH /platform/integrations/:key`.
 *
 * `key` and `category` are absent on purpose. The key names the adapter folder and is
 * referenced by every install; the category decides which `getAdapter<T>()` call
 * resolves it. Changing either would silently re-point live installs at code that does
 * not implement their contract, so the operation is "deprecate and add", not "edit".
 */
export const UpdateCatalogEntrySchema = CreateCatalogEntrySchema.omit({
    key: true,
    category: true,
}).partial();
export const CatalogEntryResponseSchema = resource(CatalogEntrySchema);
export const CatalogListResponseSchema = z.object({ data: z.array(CatalogEntrySchema) });
/* -------------------------------------------------------------------- installs */
/**
 * One catalog entry as it applies to one tenant, for the super admin's tenant page
 * (`GET /platform/tenants/:id/installs`). No credentials and no hints: platform staff
 * do not need them, so they do not get them (SECURITY.md §6.2).
 */
export const TenantInstallSchema = z.object({
    integrationKey: IntegrationKeySchema,
    name: z.string(),
    vendor: z.string().nullable(),
    category: IntegrationCategorySchema,
    planFeatureKey: PlanFeatureKeySchema,
    /** Whether the tenant's effective features currently satisfy `planFeatureKey`. */
    planAllows: z.boolean(),
    enabledByPlatform: z.boolean(),
    isEnabled: z.boolean(),
    health: InstallHealthSchema,
    lastHealthAt: z.iso.datetime().nullable(),
    /** Redacted: an error class and a short reason, never vendor text (rules §29). */
    lastError: z.string().nullable(),
    catalogStatus: CatalogStatusSchema,
});
export const TenantInstallResponseSchema = resource(TenantInstallSchema);
export const TenantInstallListResponseSchema = z.object({ data: z.array(TenantInstallSchema) });
/** `POST /platform/tenants/:id/installs/:key/disable` and `POST /admin/.../disable`. */
export const DisableInstallRequestSchema = z.object({
    reason: z.string().min(3).max(500),
});
/* ----------------------------------------------------------------------- admin */
/**
 * Display-only facts about the stored credentials (`integration_installs.cred_hint`).
 * Values are last-4s and masked addresses produced by the server; the clinic admin
 * uses them to recognise which key is installed, never to recover it.
 */
export const CredentialHintSchema = z.record(z.string().max(64), z.string().max(120));
/** The install as the clinic admin sees it (API.md §2.10). Never carries a secret. */
export const AdminInstallSchema = z.object({
    id: z.uuid(),
    enabledByPlatform: z.boolean(),
    isEnabled: z.boolean(),
    health: InstallHealthSchema,
    lastHealthAt: z.iso.datetime().nullable(),
    lastError: z.string().nullable(),
    consecutiveFailures: z.number().int().min(0),
    /** Non-secret settings, exactly as stored. */
    settings: z.record(z.string(), z.unknown()),
    credHint: CredentialHintSchema,
    credUpdatedAt: z.iso.datetime().nullable(),
    credUpdatedBy: z.string().nullable(),
    /** True once a credential bundle exists; the bundle itself is never returned. */
    credentialsSet: z.boolean(),
    /**
     * Where the vendor should POST, when this adapter has webhooks. Contains the
     * install's `webhook_key`, which is a capability: it is shown to the clinic admin
     * who configures the vendor, and to nobody else.
     */
    webhookUrl: z.string().nullable(),
});
/** The catalog half of an admin row: what the connect form renders itself from. */
export const AdminCatalogEntrySchema = CatalogEntrySchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const AdminIntegrationSchema = z.object({
    integration: AdminCatalogEntrySchema,
    /** Null when the platform has not enabled this integration for the tenant. */
    install: AdminInstallSchema.nullable(),
});
export const AdminIntegrationListResponseSchema = z.object({
    data: z.array(AdminIntegrationSchema),
});
export const AdminIntegrationResponseSchema = resource(AdminIntegrationSchema);
/**
 * `PUT /admin/integrations/:key/credentials` (step-up, no-impersonation).
 *
 * The values are validated against the catalog's `credentialSchema` by the service,
 * not here: the shape is per integration and lives in the database. What this schema
 * owns is the envelope and its size, so a malformed body is a 400 before any key
 * material is touched.
 */
export const PutCredentialsRequestSchema = z.object({
    credentials: z.record(z.string().min(1).max(64), z.union([z.string(), z.number(), z.boolean()])),
    /** Optional: connect and configure in one call, the way the connect form submits. */
    settings: z.record(z.string().min(1).max(64), z.unknown()).optional(),
});
/** `PATCH /admin/integrations/:key/settings`. Non-secret values only. */
export const PatchSettingsRequestSchema = z.object({
    settings: z.record(z.string().min(1).max(64), z.unknown()),
});
/** `POST /admin/integrations/:key/test`. */
export const HealthProbeSchema = z.object({
    result: InstallHealthSchema,
    latencyMs: z.number().int().min(0),
    message: z.string().max(500).nullable(),
    checkedAt: z.iso.datetime(),
    errorClass: z.string().max(64).nullable(),
});
export const TestConnectionResponseSchema = z.object({
    data: z.object({ health: HealthProbeSchema, install: AdminInstallSchema }),
});
/* ------------------------------------------------------------- logs and health */
/** One row of `integration_calls`, redacted (API.md §2.10, §2.13). */
export const IntegrationCallSchema = z.object({
    id: z.uuid(),
    integrationKey: IntegrationKeySchema,
    operation: z.string().max(120),
    status: IntegrationCallStatusSchema,
    httpStatus: z.number().int().nullable(),
    latencyMs: z.number().int().nullable(),
    errorClass: z.string().max(64).nullable(),
    correlationId: z.string().nullable(),
    jobId: z.string().nullable(),
    /** Counts, sizes and opaque ids only. Never a request or response body. */
    summary: z.record(z.string(), z.unknown()),
    startedAt: z.iso.datetime(),
    /** Present only on the platform (cross-tenant) listing. */
    tenantId: z.uuid().optional(),
});
export const IntegrationCallListResponseSchema = paginated(IntegrationCallSchema);
export const IntegrationCallQuerySchema = z.object({
    status: IntegrationCallStatusSchema.optional(),
    operation: z.string().max(120).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(200).optional(),
});
/** The platform listing adds a tenant and an integration filter (API.md §2.13). */
export const PlatformIntegrationCallQuerySchema = IntegrationCallQuerySchema.extend({
    tenantId: z.uuid().optional(),
    integrationKey: IntegrationKeySchema.optional(),
});
/** One row of `integration_health_checks`. */
export const IntegrationHealthCheckSchema = z.object({
    id: z.uuid(),
    result: HealthCheckResultSchema,
    latencyMs: z.number().int().nullable(),
    errorClass: z.string().max(64).nullable(),
    message: z.string().max(500).nullable(),
    checkedAt: z.iso.datetime(),
});
export const IntegrationHealthListResponseSchema = paginated(IntegrationHealthCheckSchema);
/* -------------------------------------------------------------------- webhooks */
/**
 * What a vendor gets back. Deliberately contentless: a webhook endpoint that reports
 * what it did tells an attacker with a stolen URL whether an event id already existed.
 */
export const WebhookAckSchema = z.object({ received: z.literal(true) });
//# sourceMappingURL=integrations.js.map